import React, { Fragment, JSXElementConstructor, ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { Listbox as HeadlessListBox, ListboxProps as HeadlessListboxProps } from "@headlessui/react";
import { useColorScheme } from "@ugitgud/legos/hooks/useColorScheme";
import {
  WithColorScheme,
  WithClassName,
  AnchorPropContext,
  AnchorType,
  WithFloatingAnchor,
  WithPortal,
  BaseOptionValueType,
  XOR,
} from "@ugitgud/legos/ui/shared";
import { DropdownColorSchemes, DropdownOptionType } from "@ugitgud/legos/ui/form/dropdown/types";

export type BaseDropdownProps = WithColorScheme<WithClassName<{}>, DropdownColorSchemes>;

export type DropdownExtendedProps<T extends DropdownOptionType<BaseOptionValueType> | DropdownOptionType<BaseOptionValueType>[]> =
  Omit<HeadlessListboxProps<React.ExoticComponent<BaseDropdownProps>, T, T>, "as"> &
    BaseDropdownProps &
    WithFloatingAnchor &
    WithPortal;

export type ConnectedDropdownProps<
  T extends DropdownOptionType<BaseOptionValueType> | DropdownOptionType<BaseOptionValueType>[],
> = DropdownExtendedProps<T> & {
  placeholder?: React.ReactNode;
};

type DropdownContextType = AnchorPropContext & WithPortal;
export const DropdownContext = React.createContext<DropdownContextType>({
  anchor: null,
  setReference: () => undefined,
  hasAnchorProp: false,
});

export const Dropdown = React.forwardRef(function <
  T extends DropdownOptionType<BaseOptionValueType> | DropdownOptionType<BaseOptionValueType>[],
>(
  { anchor, className, onChange, colorScheme, children, value, defaultValue, portal, ...props }: ConnectedDropdownProps<T>,
  ref?: React.ForwardedRef<HTMLDivElement>,
) {
  const [anchorDefault, setAnchor] = useState<AnchorType>(anchor);

  const scheme = useColorScheme({
    classes: {
      secondary: "dark:text-lavender-50 rounded-sm dark:bg-purple-100 h-9 outline-none  flex items-center justify-start text-xs",
    },
    common: "relative leading-none w-full",
    default: "secondary",
    className: className,
    scheme: colorScheme,
  });

  return (
    <DropdownContext.Provider
      value={{ anchor: anchor || anchorDefault, setReference: setAnchor, hasAnchorProp: typeof anchor !== "undefined", portal }}
    >
      <div ref={ref} className={scheme}>
        <HeadlessListBox {...props} onChange={onChange} value={value} defaultValue={value}>
          {children}
        </HeadlessListBox>
      </div>
    </DropdownContext.Provider>
  );
});
